import {
  OPPORTUNITY_STAGES,
  formatDisplayReference,
  formatLeadReference,
  type Opportunity,
  type OpportunityActivity,
  type OpportunityStage,
  type OwnershipChange,
  type StageChange,
} from "@/data/opportunityTypes";
import type { ProjectType } from "@/data/types";
import type { ContractDocument, DepositInfo, PricingStatus, QuoteScenario } from "@/data/quoteTypes";
import type { SpeedToLeadSummary } from "@/data/speedToLead";
import { LEAD_STAGE_FROM_DB } from "@/mappers/lead.mapper";
import { hashFilters } from "@/lib/queryKeys";
import { observeQueryStart, recordQueryMetric } from "@/lib/queryObservability";
import { supabase } from "@/lib/supabase";

export interface PageResponse<T> {
  data: T[];
  count: number;
  error: unknown | null;
}

export interface PipelineLeadsQuery {
  refreshToken?: number;
}

export interface PipelineLeadsPageQuery extends PipelineLeadsQuery {
  page: number;
  pageSize: number;
  search?: string;
  rep?: string;
  stage?: OpportunityStage | "all";
}

type PipelineLeadRow = {
  organization_id?: string | null;
  id?: string | null;
  customer_id?: string | null;
  source_name?: string | null;
  primary_name?: string | null;
  secondary_name?: string | null;
  primary_phone?: string | null;
  secondary_phone?: string | null;
  primary_email?: string | null;
  secondary_email?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  project_type?: string | null;
  lead_source?: string | null;
  lead_cost?: number | null;
  stage?: string | null;
  appointment_time?: string | null;
  assigned_rep?: string | null;
  ownership_history?: unknown[] | null;
  activities?: unknown[] | null;
  stage_history?: unknown[] | null;
  linked_quote_id?: string | null;
  linked_job_id?: string | null;
  notes?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  decision_status?: string | null;
  validation_status?: string | null;
  rejection_reason?: string | null;
  rejection_notes?: string | null;
  accepted_at?: string | null;
  rejected_at?: string | null;
  received_at?: string | null;
  is_duplicate?: boolean | null;
  matched_duplicate_by?: string | null;
  status?: string | null;
  quote_id?: string | null;
  quote_number?: string | null;
  quote_name?: string | null;
  display_reference?: string | null;
  total_estimate?: number | null;
  project_config?: Record<string, unknown> | null;
  scope_of_work?: string | null;
  deposit?: Record<string, unknown> | null;
  contract?: Record<string, unknown> | null;
  scenarios?: Record<string, unknown>[] | null;
  pricing_status?: string | null;
  won_at?: string | null;
  lost_at?: string | null;
  lost_reason?: string | null;
  locked_at?: string | null;
  locked_by?: string | null;
  presented_at?: string | null;
  quote_created_by?: string | null;
};

type PipelineSpeedToLeadSummaryRow = {
  avg_response_time_sec?: number | null;
  median_response_time_sec?: number | null;
  instant_count?: number | null;
  on_time_count?: number | null;
  at_risk_count?: number | null;
  missed_count?: number | null;
  unresponded_count?: number | null;
  total_leads?: number | null;
  sla_compliance_rate?: number | null;
};

const PIPELINE_LEAD_COLUMNS = [
  "organization_id",
  "id",
  "customer_id",
  "source_name",
  "primary_name",
  "secondary_name",
  "primary_phone",
  "secondary_phone",
  "primary_email",
  "secondary_email",
  "address",
  "city",
  "state",
  "zip",
  "project_type",
  "lead_source",
  "lead_cost",
  "stage",
  "appointment_time",
  "assigned_rep",
  "ownership_history",
  "activities",
  "stage_history",
  "linked_quote_id",
  "linked_job_id",
  "notes",
  "created_at",
  "updated_at",
  "decision_status",
  "validation_status",
  "rejection_reason",
  "rejection_notes",
  "accepted_at",
  "rejected_at",
  "received_at",
  "is_duplicate",
  "matched_duplicate_by",
  "status",
  "quote_id",
  "quote_number",
  "quote_name",
  "display_reference",
  "total_estimate",
  "project_config",
  "scope_of_work",
  "deposit",
  "contract",
  "scenarios",
  "pricing_status",
  "won_at",
  "lost_at",
  "lost_reason",
  "locked_at",
  "locked_by",
  "presented_at",
  "quote_created_by",
].join(", ");

const PIPELINE_SPEED_TO_LEAD_SUMMARY_COLUMNS = [
  "avg_response_time_sec",
  "median_response_time_sec",
  "instant_count",
  "on_time_count",
  "at_risk_count",
  "missed_count",
  "unresponded_count",
  "total_leads",
  "sla_compliance_rate",
].join(", ");

export const EMPTY_SPEED_TO_LEAD_SUMMARY: SpeedToLeadSummary = {
  avgResponseTimeSec: 0,
  medianResponseTimeSec: 0,
  instantCount: 0,
  onTimeCount: 0,
  atRiskCount: 0,
  missedCount: 0,
  unrespondedCount: 0,
  totalLeads: 0,
  slaComplianceRate: 100,
};

function clampPage(query: { page: number; pageSize: number }) {
  const page = Math.max(0, Number(query.page) || 0);
  const pageSize = Math.max(1, Math.min(Number(query.pageSize) || 25, 5000));
  return { page, pageSize, from: page * pageSize, to: page * pageSize + pageSize - 1 };
}

function sanitizeLike(value: string): string {
  return value.replace(/[%_]/g, "").replace(/,/g, " ").trim();
}

function applySearch(query: any, search: string | undefined) {
  const term = sanitizeLike(String(search ?? ""));
  if (!term) return query;
  return query.or([
    "primary_name",
    "primary_phone",
    "primary_email",
    "address",
    "city",
    "state",
    "zip",
    "project_type",
    "lead_source",
    "source_name",
    "quote_number",
    "quote_name",
  ].map((column) => `${column}.ilike.%${term}%`).join(","));
}

function applyFilters(query: any, queryInput: Pick<PipelineLeadsPageQuery, "search" | "rep" | "stage">) {
  let next = query;
  if (queryInput.stage && queryInput.stage !== "all") next = next.eq("stage", queryInput.stage);
  const rep = asString(queryInput.rep);
  if (rep && rep !== "all") next = next.eq("assigned_rep", rep);
  return applySearch(next, queryInput.search);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asString(value: unknown, fallback = ""): string {
  const normalized = String(value ?? "").trim();
  return normalized || fallback;
}

function asNumber(value: unknown, fallback = 0): number {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
}

function normalizeStage(value: unknown): OpportunityStage {
  const stage = asString(value);
  if ((OPPORTUNITY_STAGES as string[]).includes(stage)) return stage as OpportunityStage;
  return LEAD_STAGE_FROM_DB[stage] ?? "Lead";
}

function normalizeStageHistory(history: unknown): StageChange[] {
  if (!Array.isArray(history)) return [];
  return history.flatMap((item) => {
    if (!isRecord(item)) return [];
    const toStage = normalizeStage(item.toStage ?? item.to_stage);
    return [{
      id: asString(item.id, `sh-${asString(item.changedAt ?? item.changed_at, Date.now())}`),
      fromStage: item.fromStage == null && item.from_stage == null ? null : normalizeStage(item.fromStage ?? item.from_stage),
      toStage,
      changedAt: asString(item.changedAt ?? item.changed_at),
      changedBy: asString(item.changedBy ?? item.changed_by),
    }];
  });
}

function normalizeOwnershipHistory(history: unknown): OwnershipChange[] {
  if (!Array.isArray(history)) return [];
  return history.flatMap((item) => {
    if (!isRecord(item)) return [];
    const toRep = asString(item.toRep ?? item.to_rep);
    if (!toRep) return [];
    return [{
      id: asString(item.id, `own-${asString(item.changedAt ?? item.changed_at, Date.now())}`),
      fromRep: asString(item.fromRep ?? item.from_rep) || null,
      toRep,
      changedAt: asString(item.changedAt ?? item.changed_at),
      changedBy: asString(item.changedBy ?? item.changed_by),
    }];
  });
}

function normalizeActivities(activities: unknown): OpportunityActivity[] {
  if (!Array.isArray(activities)) return [];
  return activities.flatMap((item) => {
    if (!isRecord(item)) return [];
    const description = asString(item.description);
    if (!description) return [];
    return [{
      id: asString(item.id, `act-${asString(item.createdAt ?? item.created_at, Date.now())}`),
      type: asString(item.type, "system") as OpportunityActivity["type"],
      description,
      createdAt: asString(item.createdAt ?? item.created_at),
      createdBy: asString(item.createdBy ?? item.created_by),
    }];
  });
}

function displayReferenceFor(row: PipelineLeadRow): string {
  const leadId = asString(row.id);
  const raw = asString(row.display_reference ?? row.quote_number ?? row.quote_name);
  if (!raw || raw === leadId) return formatLeadReference(leadId);
  return formatDisplayReference(raw);
}

function mapPipelineLead(row: PipelineLeadRow): Opportunity {
  const id = asString(row.id);
  const stage = normalizeStage(row.stage);
  const linkedQuoteId = asString(row.quote_id ?? row.linked_quote_id);
  const fullAddress = [row.address, row.city, [row.state, row.zip].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ");

  return {
    quote_number: row.quote_number ?? undefined,
    quoteId: linkedQuoteId,
    leadId: id,
    id,
    displayReference: displayReferenceFor(row),
    primaryName: asString(row.primary_name, "Unknown"),
    secondaryName: asString(row.secondary_name),
    primaryPhone: asString(row.primary_phone),
    secondaryPhone: asString(row.secondary_phone),
    primaryEmail: asString(row.primary_email),
    secondaryEmail: asString(row.secondary_email),
    address: asString(row.address),
    city: asString(row.city),
    state: asString(row.state),
    zip: asString(row.zip),
    projectType: asString(row.project_type, "Other") as ProjectType,
    leadSource: asString(row.lead_source ?? row.source_name),
    leadCost: row.lead_cost ?? undefined,
    stage,
    stageHistory: normalizeStageHistory(row.stage_history),
    assignedRep: asString(row.assigned_rep ?? row.quote_created_by),
    ownershipHistory: normalizeOwnershipHistory(row.ownership_history),
    activities: normalizeActivities(row.activities),
    appointmentTime: asString(row.appointment_time) || undefined,
    projectConfig: (row.project_config ?? undefined) as Opportunity["projectConfig"],
    totalEstimate: asNumber(row.total_estimate),
    scopeOfWork: asString(row.scope_of_work) || undefined,
    pricingStatus: (asString(row.pricing_status) || undefined) as PricingStatus | undefined,
    deposit: (row.deposit ?? undefined) as DepositInfo | undefined,
    contract: (row.contract ?? undefined) as ContractDocument | undefined,
    scenarios: (row.scenarios ?? undefined) as QuoteScenario[] | undefined,
    linkedJobId: asString(row.linked_job_id) || undefined,
    createdAt: asString(row.created_at, new Date().toISOString()),
    updatedAt: asString(row.updated_at ?? row.created_at, new Date().toISOString()),
    notes: asString(row.notes),
    wonAt: asString(row.won_at) || undefined,
    lostAt: asString(row.lost_at) || undefined,
    lostReason: asString(row.lost_reason) || undefined,
    lockedAt: asString(row.locked_at) || undefined,
    lockedBy: asString(row.locked_by) || undefined,
    presentedAt: asString(row.presented_at) || undefined,
    customerName: asString(row.primary_name, "Unknown"),
    customerPhone: asString(row.primary_phone),
    customerEmail: asString(row.primary_email),
    customerAddress: fullAddress,
    customerId: asString(row.customer_id) || undefined,
    organizationId: asString(row.organization_id) || undefined,
    createdBy: asString(row.quote_created_by ?? row.assigned_rep),
    status: stage,
    quoteName: asString(row.quote_name),
    convertedJobId: asString(row.linked_job_id) || undefined,
    linkedQuoteId: linkedQuoteId || undefined,
    decisionStatus: row.decision_status ?? null,
    validationStatus: row.validation_status ?? null,
    rejectionReason: row.rejection_reason ?? null,
    rejectionNotes: row.rejection_notes ?? null,
    acceptedAt: row.accepted_at ?? null,
    rejectedAt: row.rejected_at ?? null,
    isDuplicate: row.is_duplicate ?? null,
    matchedDuplicateBy: row.matched_duplicate_by ?? null,
  };
}

function mapSpeedToLeadSummary(row: PipelineSpeedToLeadSummaryRow | null | undefined): SpeedToLeadSummary {
  if (!row) return EMPTY_SPEED_TO_LEAD_SUMMARY;
  return {
    avgResponseTimeSec: asNumber(row.avg_response_time_sec),
    medianResponseTimeSec: asNumber(row.median_response_time_sec),
    instantCount: asNumber(row.instant_count),
    onTimeCount: asNumber(row.on_time_count),
    atRiskCount: asNumber(row.at_risk_count),
    missedCount: asNumber(row.missed_count),
    unrespondedCount: asNumber(row.unresponded_count),
    totalLeads: asNumber(row.total_leads),
    slaComplianceRate: asNumber(row.sla_compliance_rate, 100),
  };
}

function recordPageMetric(key: string, startedAt: number, payload: unknown) {
  let payloadBytes = 0;
  try {
    payloadBytes = JSON.stringify(payload ?? null).length;
  } catch {
    payloadBytes = 0;
  }
  recordQueryMetric({ key, durationMs: Date.now() - startedAt, payloadBytes, cacheHit: false });
}

async function withPageMetrics<T>(key: string, run: () => Promise<T>): Promise<T> {
  observeQueryStart(key);
  const startedAt = Date.now();
  try {
    const result = await run();
    recordPageMetric(key, startedAt, result);
    return result;
  } catch (error) {
    recordPageMetric(key, startedAt, { error: String(error) });
    throw error;
  }
}

export const PipelinePageService = {
  async getLeads(query: PipelineLeadsQuery = {}): Promise<PageResponse<Opportunity>> {
    return this.getLeadsPage({ ...query, page: 0, pageSize: 5000, stage: "all" });
  },

  async getLeadsPage(query: PipelineLeadsPageQuery): Promise<PageResponse<Opportunity>> {
    const { from, to } = clampPage(query);
    const key = `pipeline.leads-page.${hashFilters(query)}`;
    try {
      return await withPageMetrics(key, async () => {
        let request = supabase
          .from("v_pipeline_leads_ui")
          .select(PIPELINE_LEAD_COLUMNS, { count: "exact" })
          .order("sort_at", { ascending: false, nullsFirst: false })
          .range(from, to);
        request = applyFilters(request, query);
        const { data, error, count } = await request;
        if (error) throw error;
        return { data: ((data ?? []) as PipelineLeadRow[]).map(mapPipelineLead), count: count ?? 0, error: null };
      });
    } catch (error) {
      console.warn("PipelinePageService.getLeads failed:", error);
      return { data: [], count: 0, error };
    }
  },

  async getSpeedToLeadSummary(query: PipelineLeadsQuery = {}): Promise<{ data: SpeedToLeadSummary; error: unknown | null }> {
    const key = `pipeline.speed-to-lead-summary.${hashFilters(query)}`;
    try {
      return await withPageMetrics(key, async () => {
        const { data, error } = await supabase
          .from("v_pipeline_speed_to_lead_summary")
          .select(PIPELINE_SPEED_TO_LEAD_SUMMARY_COLUMNS)
          .maybeSingle();
        if (error) throw error;
        return { data: mapSpeedToLeadSummary(data as PipelineSpeedToLeadSummaryRow | null), error: null };
      });
    } catch (error) {
      console.warn("PipelinePageService.getSpeedToLeadSummary failed:", error);
      return { data: EMPTY_SPEED_TO_LEAD_SUMMARY, error };
    }
  },
};
